import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const PLANS = [
  { id: "weekly", title: "Weekly", price: 129, suffix: "/visit", badge: "Most popular", recurring: true,
    perks: ["Full bedroom cleaning", "Trash removed", "Bed making", "Dusting + vacuuming", "Weekly report with photos"] },
  { id: "biweekly", title: "Bi-Weekly", price: 89, suffix: "/visit", recurring: true,
    perks: ["Full bedroom cleaning", "Trash removed", "Mopping & vacuuming", "Photo report each visit"] },
  { id: "monthly", title: "Monthly", price: 59, suffix: "/visit", recurring: true,
    perks: ["Refresh clean", "Light dusting + vacuuming", "Trash removed", "Photo report"] },
];

const ONE_TIME = [
  { id: "one_time", title: "One-Time Cleaning", price: 99, d: "A single deep refresh — no commitment." },
  { id: "move_in", title: "Move-In Deep Clean", price: 179, d: "Spotless first day for your student." },
  { id: "move_out", title: "Move-Out Deep Clean", price: 199, d: "Save your security deposit." },
];

const ADDONS = [
  { t: "Kitchen common area", p: 30 },
  { t: "Living room common area", p: 25 },
  { t: "Shared bathroom", p: 25 },
  { t: "Full common area", p: 65 },
  { t: "Laundry folding", p: 15 },
  { t: "Refrigerator clean", p: 20 },
  { t: "Oven clean", p: 25 },
  { t: "Deep cleaning boost", p: 45 },
];

export default function Pricing() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <Badge variant="outline" className="mb-3">Pricing</Badge>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary">Simple plans. No surprises.</h1>
        <p className="mt-4 text-muted-foreground">Per-bedroom pricing. Cancel or reschedule anytime.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-16">
        {PLANS.map((p) => (
          <Card key={p.id} data-testid={`plan-card-${p.id}`} className={`relative border-border/60 ${p.badge ? "ring-2 ring-secondary/40 shadow-lg" : ""}`}>
            {p.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary">{p.badge}</Badge>
              </div>
            )}
            <CardContent className="p-7 space-y-5">
              <div>
                <div className="font-display font-semibold text-lg text-primary">{p.title}</div>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="font-display text-5xl font-bold text-primary">${p.price}</span>
                  <span className="text-muted-foreground">{p.suffix}</span>
                </div>
              </div>
              <ul className="space-y-2">
                {p.perks.map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm">
                    <Check size={16} className="text-secondary mt-0.5 flex-shrink-0" /> <span>{x}</span>
                  </li>
                ))}
              </ul>
              <Button asChild className="w-full bg-primary hover:bg-primary/90" data-testid={`select-plan-${p.id}`}>
                <Link to={`/booking?plan=${p.id}`}>Choose {p.title}</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <h2 className="font-display text-2xl font-semibold text-primary mb-6">One-time & Move services</h2>
      <div className="grid md:grid-cols-3 gap-5 mb-16">
        {ONE_TIME.map((p) => (
          <Card key={p.id} className="border-border/60">
            <CardContent className="p-6 space-y-3">
              <div className="font-display font-semibold text-primary">{p.title}</div>
              <div className="font-display text-3xl font-bold text-primary">${p.price}</div>
              <p className="text-sm text-muted-foreground">{p.d}</p>
              <Button asChild variant="outline" className="w-full" data-testid={`select-plan-${p.id}`}>
                <Link to={`/booking?plan=${p.id}`}>Book {p.title}</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <h2 className="font-display text-2xl font-semibold text-primary mb-6">Optional add-ons</h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        {ADDONS.map((a) => (
          <div key={a.t} className="border border-border/60 rounded-xl p-4 bg-muted/30">
            <div className="text-sm font-semibold text-primary">{a.t}</div>
            <div className="text-secondary font-display font-bold mt-1">+${a.p}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
